import React, { useState, useEffect } from 'react';
import './DashboardPerformance.css';
import { 
  TrendingUp, 
  Users, 
  FileText, 
  Play, 
  Zap,
  RefreshCw,
  AlertCircle,
  ExternalLink,
  BarChart3,
  Target,
  Award,
  Lightbulb,
  ChevronRight,
  Eye,
  MousePointer
} from 'lucide-react';
import { usePerformance } from '../hooks/usePerformance';
import { useUGCAnalytics } from '../hooks/useUGCAnalytics';
import PerformanceChart from '../Components/PerformanceChart';
import TopBriefModal from './TopBriefModal';
import TopScriptModal from './TopScriptModal';
import TopCreatorsModal from './TopCreatorsModal';

const DashboardPerformance = ({ setActiveTab }) => {
  const [selectedTimeframe, setSelectedTimeframe] = useState('30d');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [showTopBriefModal, setShowTopBriefModal] = useState(false);
  const [showTopScriptModal, setShowTopScriptModal] = useState(false);
  const [showTopCreatorsModal, setShowTopCreatorsModal] = useState(false);
  const [selectedBrief, setSelectedBrief] = useState(null);
  const [selectedScript, setSelectedScript] = useState(null);
  const [selectedCreator, setSelectedCreator] = useState(null);

  // Use performance hook for platform data
  const {
    campaigns,
    selectedCampaign,
    performanceData,
    videoLinks,
    loading,
    error,
    aiInsights,
    campaignGoals,
    selectedCampaignData,
    setSelectedCampaign,
    generateInsights,
    addVideoLink,
    updateCampaignGoals,
    connectPlatform,
    fetchVideoAnalytics,
    detectPlatform,
    clearError
  } = usePerformance();

  // Use UGC analytics hook for external platform data
  const {
    platformConnections,
    ugcData,
    analysis: ugcAnalysis,
    loading: ugcLoading,
    error: ugcError,
    connectMetaAds,
    connectTikTok,
    disconnectPlatform,
    fetchMetaAdsUGC,
    fetchTikTokUGC,
    analyzeUGCPerformance,
    getTopPerformingUGC,
    getTopPerformingCreators,
    clearError: clearUgcError
  } = useUGCAnalytics();

  // Mock data for demonstration (replace with real data)
  const platformStats = {
    totalContent: 156,
    totalViews: 2840000,
    avgCTR: 4.2,
    avgConversion: 2.1,
    totalRevenue: 45600
  };

  const topPerformingBriefs = [
    {
      id: 1,
      title: "Summer Product Launch",
      views: 450000,
      ctr: 5.2,
      conversion: 2.8,
      contentCount: 12,
      successRate: 85
    },
    {
      id: 2,
      title: "Holiday Gift Guide",
      views: 380000,
      ctr: 4.8,
      conversion: 2.4,
      contentCount: 8,
      successRate: 78
    },
    {
      id: 3,
      title: "Product Demo Series",
      views: 320000,
      ctr: 4.1,
      conversion: 2.0,
      contentCount: 15,
      successRate: 72
    }
  ];

  const topPerformingScripts = [
    {
      id: 1,
      title: "Problem-Solution Hook",
      views: 520000,
      ctr: 5.8,
      conversion: 3.1,
      usageCount: 18,
      avgRating: 4.7
    },
    {
      id: 2,
      title: "Story-Based Opening",
      views: 410000,
      ctr: 4.9,
      conversion: 2.6,
      usageCount: 14,
      avgRating: 4.5
    },
    {
      id: 3,
      title: "Question Hook",
      views: 380000,
      ctr: 4.3,
        conversion: 2.2,
      usageCount: 22,
      avgRating: 4.3
    }
  ];

  const topPerformingCreators = [
    {
      id: 1,
      name: "Sarah Johnson",
      handle: "@sarahcreates",
      avatar: "/creatorimages/creatorpic1v2-min.png",
      totalViews: 680000,
      avgCTR: 5.1,
      contentCount: 24,
      successRate: 88,
      platforms: ["TikTok", "Instagram"]
    },
    {
      id: 2,
      name: "Mike Chen",
      handle: "@mikevideos",
      avatar: "/creatorimages/Creatorpic2v2-min.png",
      totalViews: 520000,
      avgCTR: 4.7,
      contentCount: 18,
      successRate: 82,
      platforms: ["TikTok"]
    },
    {
      id: 3,
      name: "Emma Davis",
      handle: "@emmaugc",
      avatar: "/creatorimages/creatorpic3v2-min.png",
      totalViews: 480000,
      avgCTR: 4.3,
      contentCount: 16,
      successRate: 79,
      platforms: ["Instagram", "TikTok"]
    }
  ];

  // Get insights based on campaign goal
  const getGoalBasedInsights = (campaignGoal) => {
    const insights = {
      views: {
        performance: "Your content is performing 23% above industry average for views",
        competitors: "You're outperforming 78% of competitors in reach",
        improvement: "Try trending hashtags and viral challenges to boost visibility",
        nextVideo: "Create a trending challenge video with popular hashtags to maximize reach"
      },
      engagement: {
        performance: "Your engagement rate is 15% higher than average",
        competitors: "You're in the top 20% for engagement in your niche",
        improvement: "Use story-based scripts like 'Get Ready With Me' to increase interactions",
        nextVideo: "Film a 'Day in the Life' or 'Get Ready With Me' style video to boost engagement"
      },
      conversions: {
        performance: "Your conversion rate is performing well at 2.1%",
        competitors: "You're converting 25% better than competitors",
        improvement: "Add clear CTAs in the first 5 seconds for better conversion rates",
        nextVideo: "Create a product demo with multiple CTAs throughout the video"
      },
      brand_awareness: {
        performance: "Your brand mentions have increased by 40% this month",
        competitors: "You're building awareness faster than 85% of competitors",
        improvement: "Focus on consistent brand messaging and visual identity",
        nextVideo: "Create a brand story video that showcases your values and mission"
      },
      lead_generation: {
        performance: "Your lead capture rate is 18% above average",
        competitors: "You're generating 30% more leads than competitors",
        improvement: "Use lead magnets and clear value propositions in your content",
        nextVideo: "Create a tutorial video with a downloadable guide as a lead magnet"
      },
      community: {
        performance: "Your community growth is 35% above industry average",
        competitors: "You're building community faster than 90% of competitors",
        improvement: "Engage with comments and create community-focused content",
        nextVideo: "Create a Q&A video responding to your community's questions"
      }
    };
    
    return insights[campaignGoal] || insights.views; // Default to views if no goal set
  };

  const selectedCampaignGoal = selectedCampaignData?.primaryGoal || 'views';
  const overallInsights = getGoalBasedInsights(selectedCampaignGoal);

  const handleGenerateInsights = async () => {
    if (selectedCampaign && selectedCampaign !== 'Overall') {
      await generateInsights(selectedCampaign);
    }
  };

  const handleBriefClick = (brief) => {
    setSelectedBrief(brief);
    setShowTopBriefModal(true);
  };

  const handleScriptClick = (script) => {
    setSelectedScript(script);
    setShowTopScriptModal(true);
  };

  const handleCreatorClick = (creator) => {
    setSelectedCreator(creator);
    setShowTopCreatorsModal(true);
  };

  const handleViewBrief = (brief) => {
    if (setActiveTab) {
      setActiveTab('briefs');
      // You can add logic here to navigate to the specific brief
    }
  };

  const timeframes = [
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' },
    { value: '90d', label: 'Last 90 Days' },
    { value: '1y', label: 'Last Year' }
  ];

  const filters = [
    { value: 'all', label: 'All Content' },
    { value: 'tiktok', label: 'TikTok' },
    { value: 'instagram', label: 'Instagram' },
    { value: 'facebook', label: 'Facebook' }
  ];

  return (
    <div className="dashboard-performance">
      <div className="dashboard-performance-container">
        {/* Error Display */}
        {(error || ugcError) && (
          <div className="dashboard-performance-error" onClick={error ? clearError : clearUgcError}>
            <AlertCircle size={16} />
            <span>{error || ugcError}</span>
            <span className="error-close">×</span>
          </div>
        )}

        {/* Header - Inside main container */}
        <div className="dashboard-performance-header">
        <div className="dashboard-performance-header-left">
          <h1>Performance</h1>
          <p>Track your content performance across platforms</p>
        </div>
        <div className="dashboard-performance-header-actions">
          <button 
            className="dashboard-performance-btn-secondary"
            onClick={() => setShowConnectModal(true)}
            disabled={loading}
          >
            <ExternalLink size={16} />
            Connect Platforms
          </button>
          <button 
            className="dashboard-performance-btn-primary" 
            onClick={handleGenerateInsights}
            disabled={loading}
          >
            <RefreshCw size={16} className={loading ? 'spinning' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters - Modern inspired */}
      <div className="dashboard-performance-filters">
        <div className="dashboard-performance-filter-tabs">
          <button className={`dashboard-performance-filter-tab ${selectedTimeframe === '7d' ? 'active' : ''}`} onClick={() => setSelectedTimeframe('7d')}>
            7 days
          </button>
          <button className={`dashboard-performance-filter-tab ${selectedTimeframe === '30d' ? 'active' : ''}`} onClick={() => setSelectedTimeframe('30d')}>
            30 days
          </button>
          <button className={`dashboard-performance-filter-tab ${selectedTimeframe === '90d' ? 'active' : ''}`} onClick={() => setSelectedTimeframe('90d')}>
            90 days
          </button>
        </div>
        <div className="filter-dropdowns">
        <select
          value={selectedCampaign}
          onChange={e => setSelectedCampaign(e.target.value)}
            disabled={loading}
            className="filter-select"
        >
            <option value="Overall">All Campaigns</option>
            {campaigns.map(campaign => (
              <option key={campaign.id} value={campaign.id}>{campaign.name}</option>
          ))}
        </select>
          <select
            value={selectedFilter}
            onChange={e => setSelectedFilter(e.target.value)}
            disabled={loading}
            className="filter-select"
          >
            {filters.map(filter => (
              <option key={filter.value} value={filter.value}>{filter.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Key Metrics - Earnings dashboard style */}
      <div className="dashboard-performance-metrics-grid">
        <div className="dashboard-performance-metric-card dashboard-performance-metric-primary">
          <div className="dashboard-performance-metric-header">
            <div className="dashboard-performance-metric-icon">
              <Eye size={24} />
            </div>
          </div>
          <div className="dashboard-performance-metric-content">
            <h3 className="dashboard-performance-metric-label">Total Views</h3>
            <p className="dashboard-performance-metric-value">{(platformStats.totalViews / 1000000).toFixed(1)}M</p>
            <span className="dashboard-performance-metric-subtitle">All platforms</span>
          </div>
        </div>
        <div className="dashboard-performance-metric-card">
          <div className="dashboard-performance-metric-header">
            <div className="dashboard-performance-metric-icon">
              <MousePointer size={24} />
            </div>
          </div>
          <div className="dashboard-performance-metric-content">
            <h3 className="dashboard-performance-metric-label">Avg CTR</h3>
            <p className="dashboard-performance-metric-value">{platformStats.avgCTR}%</p>
            <span className="dashboard-performance-metric-subtitle">Click-through rate</span>
          </div>
        </div>
        <div className="dashboard-performance-metric-card">
          <div className="dashboard-performance-metric-header">
            <div className="dashboard-performance-metric-icon">
              <Target size={24} />
            </div>
          </div>
          <div className="dashboard-performance-metric-content">
            <h3 className="dashboard-performance-metric-label">Avg Conversion</h3>
            <p className="dashboard-performance-metric-value">{platformStats.avgConversion}%</p>
            <span className="dashboard-performance-metric-subtitle">Conversion rate</span>
          </div>
        </div>
        <div className="dashboard-performance-metric-card">
          <div className="dashboard-performance-metric-header">
            <div className="dashboard-performance-metric-icon">
              <Play size={24} />
            </div>
          </div>
          <div className="dashboard-performance-metric-content">
            <h3 className="dashboard-performance-metric-label">Content Created</h3>
            <p className="dashboard-performance-metric-value">{platformStats.totalContent.toLocaleString()}</p>
            <span className="dashboard-performance-metric-subtitle">All time</span>
          </div>
        </div>
      </div>

      {/* Main Content - Modern inspired */}
      <div className="dashboard-performance-content">
        {/* Top Performers Section */}
        <div className="dashboard-performance-content-section">
          <div className="dashboard-performance-section-header">
            <h2>Top Performers</h2>
            <p>Your best performing content, scripts, and creators</p>
        </div>

          <div className="dashboard-performance-performers-grid">
            {/* Briefs */}
            <div className="dashboard-performance-performer-card">
              <div className="dashboard-performance-card-header">
                <FileText size={18} />
                <h3>Top Briefs</h3>
              </div>
              <div className="dashboard-performance-card-content">
                {topPerformingBriefs.map((brief, index) => (
                  <div key={brief.id} className="dashboard-performance-performer-item" onClick={() => handleBriefClick(brief)}>
                    <div className="dashboard-performance-item-rank">#{index + 1}</div>
                    <div className="dashboard-performance-item-info">
                      <h4>{brief.title}</h4>
                      <div className="dashboard-performance-item-metrics">
                        <span>{(brief.views / 1000).toFixed(0)}K views</span>
                        <span>{brief.ctr}% CTR</span>
                      </div>
                    </div>
                    <ChevronRight size={16} />
                  </div>
                ))}
              </div>
            </div>

            {/* Scripts */}
            <div className="dashboard-performance-performer-card">
              <div className="dashboard-performance-card-header">
                <FileText size={18} />
                <h3>Top Scripts</h3>
              </div>
              <div className="dashboard-performance-card-content">
                {topPerformingScripts.map((script, index) => (
                  <div key={script.id} className="dashboard-performance-performer-item" onClick={() => handleScriptClick(script)}>
                    <div className="dashboard-performance-item-rank">#{index + 1}</div>
                    <div className="dashboard-performance-item-info">
                      <h4>{script.title}</h4>
                      <div className="dashboard-performance-item-metrics">
                        <span>{(script.views / 1000).toFixed(0)}K views</span>
                        <span>{script.ctr}% CTR</span>
                      </div>
                    </div>
                    <ChevronRight size={16} />
                  </div>
                ))}
              </div>
            </div>

            {/* Creators */}
            <div className="dashboard-performance-performer-card">
              <div className="dashboard-performance-card-header">
                <Users size={18} />
                <h3>Top Creators</h3>
              </div>
              <div className="dashboard-performance-card-content">
                {topPerformingCreators.map((creator, index) => (
                  <div key={creator.id} className="dashboard-performance-performer-item" onClick={() => handleCreatorClick(creator)}>
                    <div className="dashboard-performance-item-rank">#{index + 1}</div>
                    <div className="creator-avatar">
                      <img 
                        src={creator.avatar} 
                        alt={creator.name}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="avatar-fallback">
                        {creator.name.split(' ').map(n => n[0]).join('')}
                      </div>
                    </div>
                    <div className="dashboard-performance-item-info">
                      <h4>{creator.name}</h4>
                      <p className="creator-handle">{creator.handle}</p>
                      <div className="dashboard-performance-item-metrics">
                        <span>{(creator.totalViews / 1000).toFixed(0)}K views</span>
                        <span>{creator.avgCTR}% CTR</span>
                      </div>
                    </div>
                    <ChevronRight size={16} />
                </div>
              ))}
              </div>
            </div>
          </div>
        </div>

        {/* Performance Overview */}
        <div className="dashboard-performance-content-section">
          <div className="dashboard-performance-section-header">
            <h2>Performance Overview</h2>
            <p>Key insights about your content performance for {selectedCampaignGoal === 'views' ? 'Views & Reach' : 
                selectedCampaignGoal === 'engagement' ? 'Engagement & Interactions' :
                selectedCampaignGoal === 'conversions' ? 'Conversions & Sales' :
                selectedCampaignGoal === 'brand_awareness' ? 'Brand Awareness' :
                selectedCampaignGoal === 'lead_generation' ? 'Lead Generation' :
                selectedCampaignGoal === 'community' ? 'Community Building' : 'Views & Reach'}</p>
          </div>
          <div className="dashboard-performance-overview-grid">
            <div className="dashboard-performance-overview-card positive">
              <div className="overview-icon">
                <TrendingUp size={20} />
              </div>
              <div className="overview-content">
                <h4>What's Working Well</h4>
                <p>{overallInsights.performance}</p>
              </div>
            </div>
            <div className="dashboard-performance-overview-card neutral">
              <div className="overview-icon">
                <Target size={20} />
              </div>
              <div className="overview-content">
                <h4>vs Competitors</h4>
                <p>{overallInsights.competitors}</p>
              </div>
            </div>
            <div className="dashboard-performance-overview-card improvement">
              <div className="overview-icon">
                <Lightbulb size={20} />
              </div>
              <div className="overview-content">
                <h4>Areas to Improve</h4>
                <p>{overallInsights.improvement}</p>
              </div>
            </div>
            <div className="dashboard-performance-overview-card action">
              <div className="overview-icon">
                <Play size={20} />
              </div>
              <div className="overview-content">
                <h4>Next Video Idea</h4>
                <p>{overallInsights.nextVideo}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Performance Chart - Modern inspired */}
        <div className="dashboard-performance-content-section">
          <div className="dashboard-performance-section-header">
            <h2>Performance Trends</h2>
            <p>View performance over time</p>
        </div>
          <div className="dashboard-performance-chart-container">
            <PerformanceChart
              data={[
                { label: 'Week 1', value: 120000 },
                { label: 'Week 2', value: 180000 },
                { label: 'Week 3', value: 220000 },
                { label: 'Week 4', value: 280000 }
              ]}
              title="Views Over Time"
              type="line"
            />
          </div>
        </div>
      </div>

      {/* Modals */}
      {showTopBriefModal && selectedBrief && (
        <TopBriefModal
          brief={selectedBrief}
          onClose={() => setShowTopBriefModal(false)}
          onViewBrief={handleViewBrief}
        />
      )}

      {showTopScriptModal && selectedScript && (
        <TopScriptModal
          script={selectedScript}
          onClose={() => setShowTopScriptModal(false)}
          onViewBrief={handleViewBrief}
        />
      )}

      {showTopCreatorsModal && selectedCreator && (
        <TopCreatorsModal
          creator={selectedCreator}
          onClose={() => setShowTopCreatorsModal(false)}
        />
      )}
      </div>
    </div>
  );
};

export default DashboardPerformance;
